// frontend/src/modules/workspace/components/RoleEditorDialog.tsx
//
// Create a custom role, or edit an existing custom role (rename + description
// + permission checkbox editor grouped by module). System roles never reach
// this dialog (they're read-only in the page).
//
// Create  → POST /roles  (with key/name/description/permissions)
// Edit     → PATCH /roles/:id (name/description) + PUT /roles/:id/permissions
//            (the page only sends a permissions update when they changed)

import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { workspaceApi } from '../api';
import type { WorkspaceRole, WorkspacePermission } from '../types';
import { CheckboxRow } from './CheckboxRow';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** null = create a new role; otherwise edit the given (custom) role. */
  role: WorkspaceRole | null;
  permissions: WorkspacePermission[];
  /** Friendly module labels for section headers. */
  moduleLabels: Record<string, string>;
  onSaved: () => void;
}

// Backend key regex: lowercase letter then [a-z0-9_], 2–41 chars total.
const KEY_RE = /^[a-z][a-z0-9_]{1,40}$/;

export function RoleEditorDialog({ open, onOpenChange, role, permissions, moduleLabels, onSaved }: Props) {
  const isEdit = role !== null;

  const [key, setKey] = React.useState('');
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (role) {
      setKey(role.key);
      setName(role.name);
      setDescription(role.description ?? '');
      setSelected(new Set(role.permissions));
    } else {
      setKey(''); setName(''); setDescription(''); setSelected(new Set());
    }
  }, [open, role]);

  function togglePerm(permKey: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permKey)) next.delete(permKey);
      else next.add(permKey);
      return next;
    });
  }

  // Group permissions by module_key, preserving first-seen order.
  const grouped = React.useMemo(() => {
    const map = new Map<string, WorkspacePermission[]>();
    for (const p of permissions) {
      const list = map.get(p.module_key) ?? [];
      list.push(p);
      map.set(p.module_key, list);
    }
    return Array.from(map.entries());
  }, [permissions]);

  async function save() {
    if (busy) return;
    if (!name.trim()) { toast.error('Role name is required'); return; }
    if (!isEdit && !KEY_RE.test(key.trim())) {
      toast.error('Invalid key', { description: 'Lowercase letter then letters, digits or underscores (2–41 chars).' });
      return;
    }
    setBusy(true);
    try {
      if (isEdit && role) {
        // Rename/description only if changed.
        if (name.trim() !== role.name || (description.trim() || null) !== (role.description ?? null)) {
          await workspaceApi.updateRole(role.id, {
            name: name.trim(),
            description: description.trim() || undefined,
          });
        }
        // Permission diff.
        const current = new Set(role.permissions);
        const changed = current.size !== selected.size
          || Array.from(selected).some((k) => !current.has(k));
        if (changed) {
          await workspaceApi.setRolePermissions(role.id, Array.from(selected));
        }
        toast.success('Role updated');
      } else {
        await workspaceApi.createRole({
          key: key.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          permissions: Array.from(selected),
        });
        toast.success('Role created');
      }
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      const msg =
        err?.code === 'role_exists'   ? 'A role with that key already exists.' :
        err?.code === 'system_role'   ? 'System roles cannot be edited.' :
        err?.code === 'bad_permission' ? 'One of the selected permissions is not valid.' :
        (err.message ?? String(err));
      toast.error(isEdit ? 'Update failed' : 'Create failed', { description: msg });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit role' : 'Create role'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Rename the role and choose which permissions it grants.'
              : 'Custom roles let you grant exactly the permissions a team needs.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 pt-2 max-h-[70vh] overflow-y-auto scrollbar-thin pr-0.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Key<span className="text-rose-500 ml-0.5">*</span></Label>
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="finance_lead"
                disabled={isEdit}
                className={isEdit ? 'font-mono text-xs opacity-70 cursor-not-allowed' : 'font-mono text-xs'}
              />
              {!isEdit && (
                <p className="text-[11px] text-muted-foreground">Lowercase, letters/digits/underscores. Cannot change later.</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label>Name<span className="text-rose-500 ml-0.5">*</span></Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Finance Lead" />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              placeholder="What this role is for." />
          </div>

          <div className="grid gap-2">
            <Label>Permissions</Label>
            {grouped.length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">No permissions available for this workspace.</p>
            ) : (
              <div className="space-y-4">
                {grouped.map(([moduleKey, perms]) => (
                  <div key={moduleKey}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
                      {moduleLabels[moduleKey] ?? moduleKey}
                    </p>
                    <ul className="space-y-1.5">
                      {perms.map((p) => (
                        <li key={p.key}>
                          <CheckboxRow
                            compact
                            checked={selected.has(p.key)}
                            onToggle={() => togglePerm(p.key)}
                            title={p.description || p.key}
                            subtitle={p.key}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create role'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
